import React, { useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import PermissionsGate from "../../../../../utils/permissionGate";
import { parsePrerequisiteIds, resolveNetsuiteProductName } from "../../../../../utils/VersionModulesUtils";
import IconButton, { StyledActionGroup, StyledIconLink } from "../../../../library/IconButton";
import { useOutsideClick } from "../../../../library/useDropdownBehavior";
import {
    StyledActionsCell,
    StyledCell,
    StyledCheckboxCell,
    StyledCountCell,
    StyledMenu,
    StyledMenuItem,
    StyledMenuWrapper,
    StyledModuleStatusPill,
    StyledPortalCell,
    StyledPortalName,
    StyledPrereqLink,
    StyledPrereqList,
    StyledStudyPathBadge,
} from "./VersionModulesTable.styles";


const MODULE_STATUS_TONES = {
    Final: "success",
    Inactive: "warning",
};

/**
 * "Portal Name" cell: the module's portal name (linked) plus its status
 * condition badge - Marketing Name is dropped per SPEC3, combined into this
 * one cell instead. The name itself renders in the table's ordinary
 * foreground colour, not as a generic blue hyperlink - Figma doesn't style
 * it as a link, so only the underlying `Link` keeps it clickable/accessible.
 * @param {object} row Module row.
 */
export function PortalNameCell({ row }) {
    return (
        <StyledPortalCell>
            <Link to={`/program-management-center/modules/module/open/${row.Id}`} title={row.Portal}>
                <StyledPortalName>{row.Portal || "-"}</StyledPortalName>
            </Link>

            {row.module_status && (
                <StyledModuleStatusPill $tone={MODULE_STATUS_TONES[row.module_status] || "grey"}>
                    {row.module_status}
                </StyledModuleStatusPill>
            )}
        </StyledPortalCell>
    );
}

/**
 * Linked-programmes count cell: a plain centred count, or a warning icon
 * alongside "0" when the module isn't linked to any programme.
 * @param {object} row Module row, carrying `Programmes` (linked programme count).
 */
export function LinkedProgrammesCountCell({ row }) {
    const count = row.Programmes ?? 0;

    if (count === 0) {
        return (
            <StyledCountCell>
                <i className="fas fa-exclamation-triangle" aria-hidden="true" title="No linked programmes" />
                {" 0"}
            </StyledCountCell>
        );
    }

    return (
        <StyledCountCell>{count}</StyledCountCell>
    );
}

/**
 * "Study Path" cell: the assigned phase name, or a red "No Study Path"
 * warning badge - the same ready/warning rule the Versions list cards use.
 * @param {object} row Module row, carrying `studyPath` (phase name or null).
 */
export function StudyPathCell({ row }) {
    const assigned = Boolean(row.studyPath);

    return (
        <StyledCell>
            <StyledStudyPathBadge $assigned={assigned}>
                <i className={`fas fa-${assigned ? "route" : "exclamation-triangle"}`} aria-hidden="true" />
                {assigned ? row.studyPath : "No Study Path"}
            </StyledStudyPathBadge>
        </StyledCell>
    );
}

/**
 * "NetSuite Product" cell: resolves the module's NetSuite product id against
 * the full NetSuite item list (the module row only carries the id).
 * @param {object} row Module row.
 * @param {Array} netsuiteList Full NetSuite item list.
 * @param {boolean} isNetsuiteLoading Whether the NetSuite item list is still loading.
 */
export function NetsuiteProductCell({ row, netsuiteList, isNetsuiteLoading }) {
    if (isNetsuiteLoading) {
        return (
            <StyledCell>
                <Skeleton width="70%" />
            </StyledCell>
        );
    }

    return (
        <StyledCell>{resolveNetsuiteProductName(netsuiteList, row.netsuite_product)}</StyledCell>
    );
}

/**
 * "Pre-Requisites" cell: resolves the module's prerequisite ids (a JSON
 * array) against this version's own module list, each opening that
 * prerequisite module.
 * @param {object} row Module row.
 * @param {Map<number, string>} moduleNameById This version's module id -> Portal name lookup.
 */
export function PreRequisitesCell({ row, moduleNameById }) {
    const history = useHistory();

    const prerequisites = parsePrerequisiteIds(row)
        .map((id) => ({ id, name: moduleNameById.get(id) }))
        .filter((prerequisite) => prerequisite.name);

    if (!prerequisites.length) {
        return (
            <StyledCell>-</StyledCell>
        );
    }

    return (
        <StyledCell>
            <StyledPrereqList>
                {prerequisites.map((prerequisite) => (
                    <StyledPrereqLink
                        key={prerequisite.id}
                        type="button"
                        onClick={() => history.push(`/program-management-center/modules/module/open/${prerequisite.id}`)}
                    >
                        {prerequisite.name}
                    </StyledPrereqLink>
                ))}
            </StyledPrereqList>
        </StyledCell>
    );
}

/**
 * The three-dot row-actions menu: currently offers "Unlink Module", running
 * through the same confirm -> progress -> cancel-remaining flow as the bulk
 * bar (a one-item batch), so there's a single unlink implementation.
 * @param {object} row Module row.
 * @param {object} hook State/handlers from `useVersionModules`.
 */
function RowActionsMenu({ row, hook }) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const menuRef = useRef(null);

    useOutsideClick(containerRef, menuRef, open, () => setOpen(false));

    return (
        <StyledMenuWrapper ref={containerRef}>
            <IconButton
                icon="ellipsis-h-alt"
                variant="solid"
                ariaLabel="Row actions"
                disabled={hook.isProcessing}
                onClick={() => setOpen((current) => !current)}
            />

            {open && (
                <StyledMenu ref={menuRef} role="menu">
                    <PermissionsGate scopes={["pmdelete"]} errorProps={{ disabled: true, title: "Insufficient Permissions" }}>
                        <StyledMenuItem
                            type="button"
                            role="menuitem"
                            disabled={hook.isLocked}
                            title={hook.isLocked ? "This version's settings are locked" : undefined}
                            onClick={() => {
                                setOpen(false);
                                hook.startConfirmation([row.Id]);
                            }}
                        >
                            <i className="fal fa-unlink" aria-hidden="true" />
                            Unlink Module
                        </StyledMenuItem>
                    </PermissionsGate>
                </StyledMenu>
            )}
        </StyledMenuWrapper>
    );
}

/**
 * "Action" cell: the folder-open link plus the three-dot row-actions menu.
 * @param {object} row Module row.
 * @param {object} hook State/handlers from `useVersionModules`.
 */
export function ActionsCell({ row, hook }) {
    return (
        <StyledActionsCell>
            <StyledActionGroup>
                <PermissionsGate
                    scopes={["pmcpmedit"]}
                    RenderError={() => <IconButton icon="folder-open" variant="solid" disabled ariaLabel="Open module" />}
                >
                    <StyledIconLink to={`/program-management-center/modules/module/open/${row.Id}`} title="Open module">
                        <IconButton icon="folder-open" variant="solid" ariaLabel="Open module" />
                    </StyledIconLink>
                </PermissionsGate>

                <RowActionsMenu row={row} hook={hook} />
            </StyledActionGroup>
        </StyledActionsCell>
    );
}

/**
 * Loading-state skeleton row matching the Modules table's column set.
 */
export function SkeletonRow() {
    return (
        <>
            <StyledCheckboxCell>
                <Skeleton width={16} height={16} />
            </StyledCheckboxCell>

            <StyledPortalCell><Skeleton width="70%" /></StyledPortalCell>
            <StyledCell><Skeleton /></StyledCell>
            <StyledCell><Skeleton /></StyledCell>
            <StyledCell><Skeleton /></StyledCell>
            <StyledCell><Skeleton /></StyledCell>
            <StyledCell><Skeleton /></StyledCell>
            <StyledCountCell><Skeleton /></StyledCountCell>
            <StyledCell><Skeleton /></StyledCell>
            <StyledCell><Skeleton /></StyledCell>
            <StyledCell><Skeleton /></StyledCell>
        </>
    );
}
